import React, {SyntheticEvent} from "react";
import {observer} from "mobx-react";
import {
    FlightsDatesSelector
} from "../../../../services/flight-search/dates-selection/flights-dates-selector";
import styled from "styled-components";
import {useServices} from "../../../../hooks/use-services.hook";
import {BOX_BORDER_RADIUS_PX, HORIZONTAL_SPACING_PX} from "../../../css-global-constants";
import {NullableString} from "../../../../types/nullable-types";
import {NullablePrice} from "../../../../services/currency/price";
import {PriceComponent} from "../../../price/price.component";
import {XCloseButtonComponent} from "../../../basic-componentes/x-close-button.component";
import {NullableStation} from "../../../../services/stations/station.service.interface";
import {PlaneHorizontalFilledIcon} from "../../../../assets/svg/plane-horizontal-filled.icon";

const StrategyIndicatorBox = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 4px 8px 4px 8px;
    border-radius: ${BOX_BORDER_RADIUS_PX};
    margin-right: ${HORIZONTAL_SPACING_PX};
    background-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.primaryContrast};
    line-height: 1;
    height: 100%;
    &:last-of-type {
      margin-right: 0;
    }
`

const StrategyDetailsBox = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
`

const ClearButtonBox = styled(XCloseButtonComponent)`
    margin-left: ${HORIZONTAL_SPACING_PX};
    border: none;
    background-color: ${props => props.theme.colors.primaryContrast};
    color: ${props => props.theme.colors.primary};
`

const TextBox = styled.div`
`

const FlightDesignatorContainerBox = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
`

const StationCodeBox = styled.div`
`

const PlaneIconBox = styled.div`
    margin: 0 4px 0 4px;
`

interface FlightDesignatorComponentProps {
    originStation: NullableStation;
    destinationStation: NullableStation;
}
const FlightDesignatorComponent: React.FC<FlightDesignatorComponentProps> = observer((props) => {
    if(!(props.originStation && props.destinationStation)) {
        return null;
    }

    return (
        <FlightDesignatorContainerBox>
            <StationCodeBox>
                {props.originStation.stationCode}
            </StationCodeBox>
            <PlaneIconBox>
                <PlaneHorizontalFilledIcon/>
            </PlaneIconBox>
            <StationCodeBox>
                {props.destinationStation.stationCode}
            </StationCodeBox>
        </FlightDesignatorContainerBox>
    )
});

interface DateSelectionStrategyIndicatorComponentProps {
    originStation: NullableStation;
    destinationStation: NullableStation;
    text: NullableString;
    lowFare: NullablePrice;
    onClear: () => void;
}

const DateSelectionStrategyIndicatorComponent: React.FC<DateSelectionStrategyIndicatorComponentProps> = observer((props) => {
    const services = useServices();
    if(!props.text) {
        return null;
    }

    const onClearClick = (event: SyntheticEvent) => {
        event.stopPropagation();
        props.onClear();
    }

    const renderLowFare = () => {
        if(props.lowFare) {
            return (
                <PriceComponent price={props.lowFare} decimalPartFontSize={services.theme.currentTheme.fontSize.xxxSmall}/>
            );
        }
        return null;
    }

    return (
        <StrategyIndicatorBox>
            <StrategyDetailsBox>
                <FlightDesignatorComponent originStation={props.originStation} destinationStation={props.destinationStation}/>
                <TextBox>
                    {props.text}
                </TextBox>
                {renderLowFare()}
            </StrategyDetailsBox>
            <ClearButtonBox onClick={onClearClick}/>
        </StrategyIndicatorBox>
    );
});

export const DepartureDateSelectionStrategyIndicatorComponent : React.FC<{datesSelector: FlightsDatesSelector;}> = observer((props) => {
    const services = useServices();
    const renderText = () => {
        if(props.datesSelector.searchController.departureDate) {
            return services.time.formatUserFriendlyDate(props.datesSelector.searchController.departureDate);
        } else {
            return null;
        }
    }

    const getLowFare = () => {
        const date = props.datesSelector.searchController.departureDate;
        if(date) {
            const result = props.datesSelector.getDepartureLowFare(date);
            if(result.price) {
                return result.price;
            }
        }

        return null;
    }

    const onClear = () => {
        props.datesSelector.clearDepartureDate();
    }

    return (
        <DateSelectionStrategyIndicatorComponent originStation={props.datesSelector.searchController.departureOrigin}
                                                 destinationStation={props.datesSelector.searchController.departureDestination}
                                                 onClear={onClear}
                                                 text={renderText()}
                                                 lowFare={getLowFare()}/>
    )
});

export const ReturnDateSelectionStrategyIndicatorComponent : React.FC<{datesSelector: FlightsDatesSelector;}> = observer((props) => {
    const services = useServices();
    const datesSelector = props.datesSelector;
    const searchController = props.datesSelector.searchController;
    const renderText = () => {
        if(searchController.returnDate) {
            return services.time.formatUserFriendlyDate(searchController.returnDate);
        } else {
            return null;
        }
    }

    const onClear = () => {
        datesSelector.clearReturnDate();
    }

    const getLowFare = () => {
        const date = searchController.returnDate;
        if(date) {
            const result = datesSelector.getReturnLowFare(date);
            if(result.price) {
                return result.price;
            }
        }

        return null;
    }

    return (
        <DateSelectionStrategyIndicatorComponent originStation={props.datesSelector.searchController.departureDestination}
                                                 destinationStation={props.datesSelector.searchController.departureOrigin}
                                                 onClear={onClear}
                                                 text={renderText()}
                                                 lowFare={getLowFare()}/>
    )
});
